<template>
  <div>
    <div v-if="!profileError && partner"
         class="container mx-auto grow max-w-10xl">
      <PartnerBanner :partnername="partner?.organizationName!" />
      <div class="flex flex-col mb-2 max-w-4xl mx-auto px-1">
        <div class="flex flex-col sm:flex-row gap-1 sm:gap-2 items-center">
          <div class="shrink-0 grow-0">
            <UserImage :image-id="partner?.profileImageId"
                       :size="'lg'" />
          </div>
          <div class="sm:col-span-2 flex flex-col pb-2 sm:p-2 gap-3">
            <div class="flex items-center sm:mt-3 flex-col lg:flex-row gap-2">
              <div class="flex flex-col gap-0.5">
                <p v-if="partner?.createdOn">
                  {{ $t('userprofile.created_on') }}
                  {{ $d(partner?.createdOn, 'short') }}
                </p>
                <h1>
                  {{ partner?.organizationName }}
                </h1>
                <p v-if="partner?.verified">{{ $t('userprofile.verified') }}</p>
              </div>
              <div class="flex items-center gap-2">
                <img v-if="partner?.verified"
                     class="h-[54px]"
                     src="~assets/icon/verified.svg"
                     :alt="i18n.t('accessibility.verified')" />
                <img
                  v-if="partner?.active == true"
                  class="h-[41px]"
                  src="~assets/icon/organization_active.svg"
                  :alt="i18n.t('accessibility.verified')" />
                <img v-if="partner?.active == false"
                     class="h-[51px]"
                     src="~assets/icon/organization_passive.svg"
                     :alt="i18n.t('accessibility.verified')" />
                <div>
                  <div class="relative">
                    <ButtonQuestionMark v-if="partner?.verified || partner?.active == true || partner?.active == false"
                                        class="absolute left-0 bottom-0.5">
                      <ul class="text-4xl flex flex-col gap-1">
                        <li class="flex gap-1">
                          <img
                            class="object-fit h-[20px] w-[30px] min-w-[30px]"
                            src="~assets/icon/verified.svg"
                            :alt="i18n.t('userprofile.verified')" />
                          <p class="text-base">{{ $t('userprofile.verified') }}</p>
                        </li>
                        <li class="flex gap-1"><img
                          class="object-fit h-[20px] w-[30px] min-w-[30px]"
                          src="~assets/icon/organization_active.svg"
                          :alt="i18n.t('accessibility.active')" />
                          <p class="text-base">{{ $t('organization.organization_active_explanation') }}</p>

                        </li>
                        <li class="flex gap-1">
                          <img
                            class="object-fit h-[17px] w-[30px]"
                            src="~assets/icon/organization_passive.svg"
                            :alt="i18n.t('organization.organization_passive_explanation')" />
                          <p class="text-base">{{ $t('organization.organization_passive_explanation') }}</p>
                        </li>
                      </ul>
                    </ButtonQuestionMark>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="partner?.numbers" class="grid grid-cols-1 sm:grid-cols-4 text-center">
              <div
                class="p-1 border-theme-secondary/30  border-solid border-x-[1px] border-t-[1px] sm:border-r-0 sm:border-b-[1px] flex-col items-center">
                <div>
                  {{ partner?.numbers.numberOfSpots }}
                </div>
                <div>{{ $t('partner.number_of_spots') }}</div>
              </div>
              <div
                class="p-1 border-theme-secondary/30  border-solid border-x-[1px] border-t-[1px] sm:border-r-0 sm:border-b-[1px] flex-col items-center">
                <div>
                  {{ partner?.numbers.numberOfFixedSpots }}
                </div>
                <div>{{ $t('partner.number_of_fixed_spots') }}</div>
              </div>
              <div
                class="p-1 border-theme-secondary/30 border-solid border-x-[1px] border-t-[1px] sm:border-b-[1px] flex-col items-center"
              :class="{'sm:border-r-1 border-b-[1px]': !partner?.official,
                  'sm:border-r-0 ': partner.official}">
                <div>
                  {{ partner?.numbers.numberOfComments }}
                </div>
                <div>{{ $t('partner.number_of_comments') }}</div>
              </div>
              <div v-if="partner?.official" class="p-1 border-theme-secondary/30 border-solid border-[1px] flex-col items-center">
                <div>
                  {{ partner?.numbers.numberOfOfficialAnswers }}
                </div>
                <div>{{ $t('partner.number_of_official_answers') }}</div>
              </div>
            </div>
            <div v-if="partner?.bio && partner?.bio != ''"
                 class="mt-3 bio">
              <p v-linkify:options="{
              target: '_blank',
            }">
                {{ partner?.bio }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ErrorView v-if="profileError" :error="profileError" :status="status" @retry="refresh" />
    <MoveableMap v-if="!profileError && showMap && partner"
                 :platform-name="platformName"
                 :country-code="countryCode"
                 :initial-map-bounds="mapBounds"
                 :region="partner.region"
                 :map-style="'height:400px;'"
                 :spot-locations="partner.spotLocations?.features"
                 :zoom-control="true"
                 ref="map">
    </MoveableMap>
  </div>
</template>
<script setup lang="ts">

import MoveableMap from '~/components/Map/MoveableMap.vue';
import type { MultiPolygon, Polygon } from 'geojson';
import ErrorView from '~/components/ErrorView.vue';

const props = defineProps<
  {
    name: string;
  }>();

const name = props.name;
const i18n = useI18n();
const map = ref<InstanceType<typeof MoveableMap>>();
const platformName = usePlatformSettings().platformName;
const countryCode = usePlatformSettings().countryCode;
const mapBounds = ref<MapBounds>();
const showMap = ref(false);

const { data: partner, error: profileError, refresh, status } = await useApi().asyncData<Partner>('partner/' + name);

if (partner.value && !profileError.value && partner.value.region) {
  if (partner.value.region.geometry.type === 'Polygon') {
    const bounds = geomHelpers.bboxFromPolygon(partner.value.region.geometry as Polygon);
    mapBounds.value = {
      swLat: bounds[1],
      swLon: bounds[0],
      neLat: bounds[3],
      neLon: bounds[2]
    };
  } else if (partner.value.region.geometry.type === 'MultiPolygon') {
    const bounds = geomHelpers.bboxFromMultiPolygon(partner.value.region.geometry as MultiPolygon);
    mapBounds.value = {
      swLat: bounds[1],
      swLon: bounds[0],
      neLat: bounds[3],
      neLon: bounds[2]
    };
  }

  showMap.value = true;
}
</script>
<style scoped lang="scss">
::v-deep(.bio) {
  a {
    @apply text-theme-primary;
  }
}
</style>
